import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LayoutDashboard, LogOut, MessageSquare, Home } from "lucide-react";
import { useState } from "react";
import Wordmark from "@/components/Wordmark";
import NotificationBell from "@/components/NotificationBell";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const dashPath = user
    ? user.role === "cleaner" ? "/cleaner/dashboard"
      : ["admin", "super_admin"].includes(user.role) ? "/admin" : "/customer"
    : "/login";

  return (
    <header className="glass sticky top-0 z-40 border-b border-primary/5" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <Wordmark iconClass="h-9 w-auto" textClass="text-lg sm:text-xl" />
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
          <Link to="/search" className="hover:text-accent transition-colors" data-testid="nav-find">Find Cleaners</Link>
          {user?.role === "customer" && (
            <Link to="/host" className="inline-flex items-center gap-1.5 hover:text-accent transition-colors" data-testid="nav-host-mode">
              <Home className="h-4 w-4" /> Airbnb Host Mode
            </Link>
          )}
          <a href="/#services" className="hover:text-accent transition-colors" data-testid="nav-services">Services</a>
          <a href="/#how" className="hover:text-accent transition-colors" data-testid="nav-how">How it works</a>
          <Link to="/register?role=cleaner" className="hover:text-accent transition-colors" data-testid="nav-become">Become a Cleaner</Link>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <NotificationBell />
              <Link to="/messages" className="p-2 rounded-full hover:bg-secondary transition-colors" data-testid="nav-messages">
                <MessageSquare className="h-5 w-5" />
              </Link>
              <button onClick={() => navigate(dashPath)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors" data-testid="nav-dashboard">
                <LayoutDashboard className="h-4 w-4" /> Dashboard
              </button>
              <button onClick={logout} className="p-2 rounded-full hover:bg-secondary transition-colors" data-testid="nav-logout" title="Log out">
                <LogOut className="h-5 w-5" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="px-4 py-2 text-sm font-medium hover:text-accent transition-colors" data-testid="nav-login">Log in</Link>
              <Link to="/register" className="px-5 py-2 rounded-full bg-accent text-accent-foreground text-sm font-medium hover:bg-accent/90 transition-colors" data-testid="nav-signup">Sign up</Link>
            </>
          )}
        </div>

        <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-primary/5 px-5 py-4 flex flex-col gap-3 bg-background" data-testid="mobile-menu">
          <Link to="/search" onClick={() => setOpen(false)} className="py-2">Find Cleaners</Link>
          {user?.role === "customer" && (
            <Link to="/host" onClick={() => setOpen(false)} className="py-2 inline-flex items-center gap-2 font-medium" data-testid="mobile-nav-host-mode">
              <Home className="h-4 w-4 text-accent" /> Airbnb Host Mode
            </Link>
          )}
          <Link to="/register?role=cleaner" onClick={() => setOpen(false)} className="py-2">Become a Cleaner</Link>
          {user ? (
            <>
              <button onClick={() => { navigate(dashPath); setOpen(false); }} className="text-left py-2">Dashboard</button>
              <button onClick={logout} className="text-left py-2 text-destructive">Log out</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="py-2">Log in</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="py-2 font-semibold text-accent">Sign up</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
